import React, { useState, useEffect, useMemo, useRef } from "react";

const PRIORITY = ["BTC","ETH","SOL","BNB","XRP","DOGE","ADA","AVAX","LINK","DOT","SUI","NEAR"];

const CAT_META = {
  CRYPTO:      { color: "#4ad6ff", label: "CRYPTO"      },
  MACRO:       { color: "#f5b042", label: "MACRO"       },
  FED:         { color: "#ff8a4d", label: "FED"         },
  EQUITIES:    { color: "#4ade80", label: "EQUITIES"    },
  COMMODITIES: { color: "#d4a857", label: "COMMODITY"   },
  GEO:         { color: "#ff5d6c", label: "GEO"         },
  MARKETS:     { color: "#b377ff", label: "MARKETS"     },
};

function fmtP(price) {
  if (price == null) return "—";
  if (price >= 100000) return `${(price / 1000).toFixed(1)}K`;
  if (price >= 10000)  return price.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  if (price >= 1000)   return price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (price >= 1)      return price.toFixed(3);
  return price.toFixed(5);
}

function newsAgoShort(pub) {
  if (!pub) return "";
  const s = Math.floor((Date.now() - new Date(pub).getTime()) / 1000);
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

function MomentumBar({ change }) {
  if (change == null) return <span style={{ color: "rgba(255,255,255,0.18)" }}>{"░".repeat(10)}</span>;
  const abs = Math.min(Math.abs(change), 15);
  const filled = Math.round(abs / 15 * 10);
  const isPos = change >= 0;
  const bar = Array(10).fill(0).map((_, i) => i < filled ? "█" : "░").join("");
  return <span style={{ color: isPos ? "#4ade80" : "#ff5d6c", letterSpacing: 0.5 }}>{bar}</span>;
}

function BlinkCursor() {
  const [on, setOn] = useState(true);
  useEffect(() => {
    const t = setInterval(() => setOn(v => !v), 520);
    return () => clearInterval(t);
  }, []);
  return <span style={{ color: "#4ad6ff", opacity: on ? 1 : 0 }}>█</span>;
}

export default function BloombergTerminal({ coins = [], hubState, btcPrice, news = [] }) {
  const [timeStr, setTimeStr] = useState(() =>
    new Date().toLocaleTimeString([], { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" })
  );
  const [newsIdx, setNewsIdx] = useState(0);
  const [activeCat, setActiveCat] = useState(null);
  const [flashMap, setFlashMap] = useState({});
  const prevCoinsRef = useRef({});

  useEffect(() => {
    const t = setInterval(() =>
      setTimeStr(new Date().toLocaleTimeString([], { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" }))
    , 1000);
    return () => clearInterval(t);
  }, []);

  const filteredNews = useMemo(() =>
    activeCat ? news.filter(n => n.category === activeCat) : news
  , [news, activeCat]);

  useEffect(() => {
    if (filteredNews.length < 2) return;
    const t = setInterval(() => setNewsIdx(v => (v + 1) % filteredNews.length), 5000);
    return () => clearInterval(t);
  }, [filteredNews.length]);

  useEffect(() => { setNewsIdx(0); }, [activeCat]);

  const catCounts = useMemo(() => {
    const counts = {};
    news.forEach(n => {
      const c = n.category || "CRYPTO";
      counts[c] = (counts[c] || 0) + 1;
    });
    return counts;
  }, [news]);

  // Flash detection for price changes
  useEffect(() => {
    const newFlash = {};
    coins.forEach(c => {
      const prev = prevCoinsRef.current[c.symbol];
      if (prev != null && c.price != null && c.price !== prev) {
        newFlash[c.symbol] = c.price > prev ? "up" : "dn";
      }
    });
    if (Object.keys(newFlash).length > 0) {
      setFlashMap(newFlash);
      setTimeout(() => setFlashMap({}), 600);
    }
    const map = {};
    coins.forEach(c => { if (c.symbol) map[c.symbol] = c.price; });
    prevCoinsRef.current = map;
  }, [coins]);

  const rows = useMemo(() => {
    const coinMap = {};
    coins.forEach(c => { if (c.symbol) coinMap[c.symbol] = c; });
    return PRIORITY.map(sym => {
      const c = coinMap[sym] || {};
      const price = sym === "BTC" && btcPrice ? Number(btcPrice) : (c.price ?? null);
      const chg = typeof c.change === "number" ? c.change : null;
      const vol = typeof c.volume === "number" ? c.volume : null;
      const status = chg == null ? "WAIT"
        : Math.abs(chg) > 8 ? "SURGE"
        : Math.abs(chg) > 3 ? "ACTIVE"
        : "STABLE";
      return { symbol: sym, price, change: chg, vol, status };
    });
  }, [coins, btcPrice]);

  const btcRow = rows.find(r => r.symbol === "BTC");
  const ethRow = rows.find(r => r.symbol === "ETH");
  const solRow = rows.find(r => r.symbol === "SOL");
  const regime = String(hubState?.system?.regime || "RANGING").toUpperCase();
  const health = Number(hubState?.system?.healthScore || 0);
  const heat   = Number(hubState?.exposure?.portfolioHeat || 0);
  const activeEngines = Number(hubState?.system?.activeEngines || 0);
  const currentNews = filteredNews[newsIdx] || null;

  const regimeColor = regime.includes("BULL") ? "#4ade80" : regime.includes("BEAR") ? "#ff5d6c" : "#f5b042";
  const MONO = "ui-monospace, 'JetBrains Mono', SFMono-Regular, Menlo, monospace";

  const cellStyle = (isHeader) => ({
    fontFamily: MONO,
    fontSize: isHeader ? 8 : 9,
    letterSpacing: "0.08em",
  });

  return (
    <div style={{
      background: "#000",
      border: "1px solid rgba(74,214,255,0.18)",
      borderRadius: 12,
      overflow: "hidden",
      fontFamily: MONO,
    }}>

      {/* ── Terminal header ── */}
      <div style={{
        background: "rgba(74,214,255,0.08)",
        borderBottom: "1px solid rgba(74,214,255,0.15)",
        padding: "5px 12px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ color: "#4ad6ff", fontSize: 9, fontWeight: 700, letterSpacing: "0.22em" }}>
            AEON MARKET TERMINAL
          </span>
          <span style={{ color: "rgba(255,255,255,0.22)", fontSize: 8 }}>v3.1</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ color: "#4ade80", fontSize: 8, fontWeight: 700, letterSpacing: "0.18em" }}>● LIVE</span>
          <span style={{ color: "rgba(255,255,255,0.45)", fontSize: 9 }}>{timeStr}</span>
          <BlinkCursor />
        </div>
      </div>

      {/* ── Hero price row ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
        {[btcRow, ethRow, solRow].map((row, i) => {
          if (!row) return <div key={i} />;
          const isPos = (row.change ?? 0) >= 0;
          const flash = flashMap[row.symbol];
          return (
            <div key={row.symbol} style={{
              padding: "7px 10px",
              borderRight: i < 2 ? "1px solid rgba(255,255,255,0.07)" : "none",
            }}>
              <div style={{ color: "rgba(255,255,255,0.35)", fontSize: 7, letterSpacing: "0.2em", marginBottom: 2 }}>
                {row.symbol}/USDT
              </div>
              <div style={{
                color: flash === "up" ? "#4ade80" : flash === "dn" ? "#ff5d6c" : "#fff",
                fontSize: 13,
                fontWeight: 700,
                lineHeight: 1,
                transition: "color 300ms",
              }}>
                ${fmtP(row.price)}
              </div>
              <div style={{
                color: isPos ? "#4ade80" : "#ff5d6c",
                fontSize: 8,
                fontWeight: 700,
                marginTop: 2,
              }}>
                {row.change != null ? `${isPos ? "▲" : "▼"} ${Math.abs(row.change).toFixed(2)}%` : "—"}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Regime / system bar ── */}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "4px 12px",
        background: "rgba(255,255,255,0.02)",
        borderBottom: "1px solid rgba(255,255,255,0.05)",
        fontSize: 8,
        flexWrap: "wrap",
      }}>
        <span style={{ color: "rgba(255,255,255,0.35)", letterSpacing: "0.16em" }}>REGIME:</span>
        <span style={{ color: regimeColor, fontWeight: 700 }}>{regime}</span>
        <span style={{ color: "rgba(255,255,255,0.18)" }}>│</span>
        <span style={{ color: "rgba(255,255,255,0.35)", letterSpacing: "0.16em" }}>HEALTH:</span>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <div style={{ width: 44, height: 4, background: "rgba(255,255,255,0.09)", borderRadius: 2, overflow: "hidden" }}>
            <div style={{ width: `${health}%`, height: "100%", background: health > 70 ? "#4ade80" : health > 40 ? "#f5b042" : "#ff5d6c", transition: "width 800ms" }} />
          </div>
          <span style={{ color: health > 70 ? "#4ade80" : "#f5b042" }}>{health}%</span>
        </div>
        <span style={{ color: "rgba(255,255,255,0.18)" }}>│</span>
        <span style={{ color: "rgba(255,255,255,0.35)", letterSpacing: "0.16em" }}>HEAT:</span>
        <span style={{ color: heat > 70 ? "#ff5d6c" : heat > 40 ? "#f5b042" : "#4ade80", fontWeight: 700 }}>{heat}%</span>
        <span style={{ color: "rgba(255,255,255,0.18)" }}>│</span>
        <span style={{ color: "rgba(255,255,255,0.35)", letterSpacing: "0.16em" }}>ENG:</span>
        <span style={{ color: "#4ad6ff", fontWeight: 700 }}>{activeEngines}/9</span>
      </div>

      {/* ── Data table — scrollable horizontally on narrow screens ── */}
      <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
        <div style={{ minWidth: 320 }}>
          {/* Table header */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "52px 72px 54px 58px 1fr",
            padding: "4px 12px",
            background: "rgba(74,214,255,0.04)",
            borderBottom: "1px solid rgba(255,255,255,0.05)",
            ...cellStyle(true),
          }}>
            {["SYMBOL","LAST","CHG%","STATUS","MOMENTUM"].map(h => (
              <span key={h} style={{ color: "rgba(255,255,255,0.3)", letterSpacing: "0.14em" }}>{h}</span>
            ))}
          </div>

          {/* Data rows */}
          {rows.map((row, i) => {
            const isPos = (row.change ?? 0) >= 0;
            const flash = flashMap[row.symbol];
            const statusColor = row.status === "SURGE" ? "#ff5d6c" : row.status === "ACTIVE" ? "#f5b042" : row.status === "STABLE" ? "#4ade80" : "rgba(255,255,255,0.25)";
            return (
              <div key={row.symbol}
                style={{
                  display: "grid",
                  gridTemplateColumns: "52px 72px 54px 58px 1fr",
                  padding: "4px 12px",
                  borderBottom: i < rows.length - 1 ? "1px solid rgba(255,255,255,0.04)" : "none",
                  background: flash ? (flash === "up" ? "rgba(74,222,128,0.04)" : "rgba(255,93,108,0.04)") : "transparent",
                  transition: "background 300ms",
                  ...cellStyle(false),
                }}>
                <span style={{ color: "#4ad6ff", fontWeight: 700 }}>{row.symbol}</span>
                <span style={{ color: flash === "up" ? "#4ade80" : flash === "dn" ? "#ff5d6c" : "#e6ebf2", transition: "color 300ms" }}>
                  {row.price ? "$" + fmtP(row.price) : "—"}
                </span>
                <span style={{ color: isPos ? "#4ade80" : "#ff5d6c", fontWeight: 700 }}>
                  {row.change != null ? `${isPos ? "+" : ""}${row.change.toFixed(2)}` : "—"}
                </span>
                <span style={{ color: statusColor, fontSize: 7, letterSpacing: "0.08em" }}>{row.status}</span>
                <span><MomentumBar change={row.change} /></span>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Category filter tabs ── */}
      {Object.keys(catCounts).length > 0 && (
        <div style={{
          display: "flex", gap: 4, flexWrap: "wrap", padding: "5px 12px",
          borderTop: "1px solid rgba(255,255,255,0.05)",
          background: "rgba(0,0,0,0.2)",
        }}>
          <button
            onClick={() => setActiveCat(null)}
            style={{
              padding: "2px 6px", borderRadius: 3, cursor: "pointer",
              background: !activeCat ? "rgba(74,214,255,0.12)" : "transparent",
              border: `1px solid ${!activeCat ? "rgba(74,214,255,0.5)" : "rgba(255,255,255,0.1)"}`,
              color: !activeCat ? "#4ad6ff" : "rgba(255,255,255,0.35)",
              fontSize: 7, fontFamily: MONO, fontWeight: 700, letterSpacing: "0.1em",
            }}>
            ALL {news.length}
          </button>
          {Object.entries(catCounts).sort((a,b) => b[1]-a[1]).map(([cat, cnt]) => {
            const meta = CAT_META[cat] || { color: "#8a93a3", label: cat };
            const isActive = activeCat === cat;
            return (
              <button key={cat}
                onClick={() => setActiveCat(c => c === cat ? null : cat)}
                style={{
                  padding: "2px 6px", borderRadius: 3, cursor: "pointer",
                  background: isActive ? `${meta.color}18` : "transparent",
                  border: `1px solid ${isActive ? meta.color : `${meta.color}44`}`,
                  color: meta.color, fontSize: 7, fontFamily: MONO,
                  fontWeight: 700, letterSpacing: "0.1em",
                }}>
                {meta.label} {cnt}
              </button>
            );
          })}
        </div>
      )}

      {/* ── News ticker footer ── */}
      {currentNews ? (
        <div style={{
          borderTop: `1px solid ${(CAT_META[currentNews.category] || {color:"#f5b042"}).color}22`,
          background: `${(CAT_META[currentNews.category] || {color:"#f5b042"}).color}06`,
        }}>
          {/* Main ticker row */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 12px", overflow: "hidden" }}>
            {/* Category chip */}
            {currentNews.category && (() => {
              const meta = CAT_META[currentNews.category] || { color: "#f5b042", label: currentNews.category };
              return (
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 3,
                  padding: "1px 5px", borderRadius: 3, flexShrink: 0,
                  background: `${meta.color}18`, border: `1px solid ${meta.color}55`,
                  color: meta.color, fontSize: 7, fontFamily: MONO, fontWeight: 700, letterSpacing: "0.1em",
                }}>
                  <span style={{ width: 4, height: 4, borderRadius: "50%", background: meta.color, display: "inline-block" }} />
                  {meta.label}
                </span>
              );
            })()}
            <span style={{ color: "rgba(255,255,255,0.55)", fontSize: 8, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
              {currentNews.title}
            </span>
          </div>
          {/* Sub-row: source + time + impact + counter */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "2px 12px 5px", flexWrap: "wrap" }}>
            {currentNews.source && (
              <span style={{ color: "rgba(255,255,255,0.25)", fontSize: 7, fontFamily: MONO }}>
                {currentNews.source}
              </span>
            )}
            {currentNews.published && (
              <span style={{ color: "rgba(255,255,255,0.2)", fontSize: 7, fontFamily: MONO }}>
                {newsAgoShort(currentNews.published)}
              </span>
            )}
            {(currentNews.impact || 0) >= 70 && (
              <span style={{ color: "#ff5d6c", fontSize: 7, fontFamily: MONO, fontWeight: 700, letterSpacing: "0.1em" }}>
                ▲ HIGH IMPACT
              </span>
            )}
            {(currentNews.impact || 0) >= 50 && (currentNews.impact || 0) < 70 && (
              <span style={{ color: "#f5b042", fontSize: 7, fontFamily: MONO, fontWeight: 700, letterSpacing: "0.1em" }}>
                ◆ NOTABLE
              </span>
            )}
            {currentNews.sentiment?.label && (
              <span style={{
                color: currentNews.sentiment.label === "BULLISH" ? "#4ade80"
                     : currentNews.sentiment.label === "BEARISH" ? "#ff5d6c"
                     : "rgba(255,255,255,0.25)",
                fontSize: 7, fontFamily: MONO,
              }}>
                {currentNews.sentiment.emoji} {currentNews.sentiment.label}
              </span>
            )}
            <span style={{ color: "rgba(255,255,255,0.15)", fontSize: 7, fontFamily: MONO, marginLeft: "auto" }}>
              {newsIdx + 1}/{filteredNews.length}
            </span>
          </div>
        </div>
      ) : (
        <div style={{ padding: "5px 12px", color: "rgba(255,255,255,0.18)", fontSize: 8, borderTop: "1px solid rgba(255,255,255,0.05)", fontFamily: MONO }}>
          awaiting market data…
        </div>
      )}
    </div>
  );
}
